import type { OwdspControlType } from '@adminhub/schema-types';

import type { FeatureEvents, TrackEvent } from '../new-analytics-types';
import { TrackActionSubject } from '../new-analytics-types';

// Screen/source constants
export const dataLossPreventionSimpleControlPage = 'dataLossPreventionSimpleControlPage';
export const dataLossPreventionAppAccessControlPage = 'dataLossPreventionAppAccessControlPage';

// Event type definitions
type TrackEventKeys =
  | 'dspDraftControlCreatedEvent'
  | 'dspDraftControlDeletedEvent'
  | 'dspDraftControlActivatedEvent'
  | 'dspControlDefaultConfigurationUpdatedEvent'
  | 'dspControlOverrideAddedEvent'
  | 'dspControlOverrideUpdatedEvent'
  | 'dspControlOverrideRemovedEvent'
  | 'dspCustomAppControlAddedEvent';

// Attribute interfaces
interface BaseControlAttributes {
  orgId: string;
  controlType: OwdspControlType;
}

interface AppControlAttributes extends BaseControlAttributes {
  appId?: string;
}

interface OverrideAttributes extends BaseControlAttributes {
  overrideType: string;
  resourceCount?: number;
  appId?: string;
}

interface DefaultConfigurationAttributes extends BaseControlAttributes {
  defaultConfiguration: string;
}

// Event definitions
export const dataSecurityPolicyEvents: FeatureEvents<never, never, TrackEventKeys, string> = {
  confluenceSpec: '', // Add manually later
  screen: {},
  ui: {},
  track: {
    dspDraftControlCreatedEvent: (attributes: AppControlAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'created',
        actionSubject: TrackActionSubject.DspControl,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97297',
    }),

    dspDraftControlDeletedEvent: (attributes: AppControlAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'deleted',
        actionSubject: TrackActionSubject.DspControl,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97298',
    }),

    dspDraftControlActivatedEvent: (attributes: BaseControlAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'activated',
        actionSubject: TrackActionSubject.DspControl,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97299',
    }),

    dspControlDefaultConfigurationUpdatedEvent: (attributes: DefaultConfigurationAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'updated',
        actionSubject: TrackActionSubject.DspControl,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97300',
    }),

    dspControlOverrideAddedEvent: (attributes: OverrideAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'added',
        actionSubject: TrackActionSubject.DspControlOverride,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97301',
    }),

    dspControlOverrideUpdatedEvent: (attributes: OverrideAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'updated',
        actionSubject: TrackActionSubject.DspControlOverride,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97302',
    }),

    dspControlOverrideRemovedEvent: (attributes: OverrideAttributes): TrackEvent => ({
      data: {
        source: attributes.controlType === 'appAccess' ? dataLossPreventionAppAccessControlPage : dataLossPreventionSimpleControlPage,
        action: 'removed',
        actionSubject: TrackActionSubject.DspControlOverride,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97303',
    }),

    dspCustomAppControlAddedEvent: (attributes: AppControlAttributes): TrackEvent => ({
      data: {
        source: dataLossPreventionAppAccessControlPage,
        action: 'added',
        actionSubject: TrackActionSubject.DspCustomAppControl,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97304',
    }),
  },
  operational: {},
};
